/**
 * The route guard.
 *
 * Three states, and the middle one is the one that matters: while the `/auth/me`
 * request is in flight we render the mark rather than the login screen.
 * Rendering "signed out" optimistically means every hard refresh flashes a login
 * page at someone who is signed in, and then redirects them away from the URL
 * they typed.
 */

import { useEffect, useState, useRef } from "react";
import { Navigate, Outlet, useHref, useLocation, useNavigate } from "react-router";
import { useAuthStatus, takeReturnTo } from "@/features/auth/hooks";
import { getProjectSlug } from "@/lib/api";
import { goToPath } from "@/lib/project-url";
import { Logo } from "./logo";

/** A page load to another router's URL (sign-in lives outside every project). */
function LeaveTo({ href }: { href: string }) {
  useEffect(() => window.location.replace(href), [href]);
  return null;
}

export function RequireAuth() {
  const { isResolved, isAuthenticated, unreachable, error } = useAuthStatus();
  const location = useLocation();
  const navigate = useNavigate();
  // The full browser path, project prefix included, so sign-in returns here.
  const here = useHref({ pathname: location.pathname, search: location.search, hash: location.hash });
  const inProject = getProjectSlug() !== null;

  const consumed = useRef(false);
  const [returnHandled, setReturnHandled] = useState(false);

  // Finish the journey the login screen started. The `auth` block always returns
  // the browser to one configured URL, so someone who asked for /judges lands on
  // "/" with their destination still parked in sessionStorage. Consumed here
  // rather than in LoginPage because that component never mounts on the way
  // back: the app loads already authenticated.
  //
  // Only from the root "/" (not a project's home), the one place the callback
  // can deliver to. Acting on it anywhere else would let a stale value hijack a
  // deliberate navigation.
  useEffect(() => {
    if (!isAuthenticated || inProject || location.pathname !== "/") return;
    if (consumed.current) return;
    consumed.current = true;
    const next = takeReturnTo();
    if (next !== "/") goToPath(next, navigate);
    else setReturnHandled(true);
  }, [isAuthenticated, inProject, location.pathname, navigate]);

  if (!isResolved) {
    return (
      <div role="status" aria-label="Loading" className="grid min-h-screen place-items-center bg-subtle">
        <span className="animate-pulse">
          <Logo size={36} />
        </span>
      </div>
    );
  }

  // A backend that cannot be reached is not a logout. Saying so, and offering
  // a retry, keeps the URL they asked for instead of bouncing them through a
  // sign-in that would not have fixed anything.
  if (unreachable) {
    return (
      <div className="grid min-h-screen place-items-center bg-subtle px-6">
        <div className="box flex max-w-md flex-col items-center gap-3 px-7 py-6 text-center">
          <Logo size={32} />
          <p className="text-[15px] font-semibold">The workbench is not answering.</p>
          <p className="text-[13px] leading-relaxed text-muted">
            {error instanceof Error ? error.message : "The API could not be reached."}
          </p>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Carry where they were going, so signing in lands them there instead of on
    // the dashboard. `replace` keeps the guard out of the back-button history.
    const login = `/login?next=${encodeURIComponent(here)}`;
    return inProject ? <LeaveTo href={login} /> : <Navigate to={login} replace />;
  }

  if (!inProject && location.pathname === "/" && !returnHandled) return null;
  return <Outlet />;
}
